<template>
  <div class="report-chart">
    <div
      v-if="!series.length"
      class="text-muted fs-sm py-2"
    >
      {{ emptyText }}
    </div>
    <div
      v-else
      class="report-chart-canvas"
      :style="{ height: canvasHeight }"
    >
      <canvas
        ref="canvas"
        role="img"
        :aria-label="summary"
      />
    </div>
  </div>
</template>

<script>
import {
    ArcElement,
    BarController,
    BarElement,
    CategoryScale,
    Chart,
    DoughnutController,
    Legend,
    LinearScale,
    Tooltip,
} from "chart.js";

Chart.register(ArcElement, BarController, BarElement, CategoryScale, DoughnutController, Legend, LinearScale, Tooltip);

const FALLBACK = {
    success: "#82b54b",
    danger: "#e04f1a",
    primary: "#0665d0",
    warning: "#f59c1a",
    info: "#3c90df",
    secondary: "#6c757d",
};

// Bootstrap custom properties are read at draw time so the OneUI light/dark themes keep working.
function cssVar(name, fallback) {
    if (typeof window === "undefined") return fallback;
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
}

function resolveColor(key) {
    const name = String(key || "primary").replace(/^fill-/, "");
    return cssVar("--bs-" + name, FALLBACK[name] || FALLBACK.primary);
}

function bodyColor() {
    return cssVar("--bs-body-color", "#343a40");
}

function gridColor() {
    return cssVar("--bs-border-color", "rgba(128, 128, 128, 0.25)");
}

// Dashed "min"/"avg" marker on the value axis. Configured via options.plugins.reportTarget.
const targetLinePlugin = {
    id: "reportTarget",
    afterDatasetsDraw(chart, args, opts) {
        const value = Number(opts && opts.value);
        if (!(value > 0) || !chart.scales.x) return;

        const x = chart.scales.x.getPixelForValue(value);
        const { top, bottom, right } = chart.chartArea;
        const ctx = chart.ctx;

        ctx.save();
        ctx.globalAlpha = 0.55;
        ctx.strokeStyle = bodyColor();
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.stroke();

        ctx.setLineDash([]);
        ctx.globalAlpha = 0.75;
        ctx.fillStyle = bodyColor();
        ctx.font = "11px sans-serif";
        const alignRight = x > right - 40;
        ctx.textAlign = alignRight ? "right" : "left";
        ctx.fillText(`${opts.label || "min"} ${value}`, x + (alignRight ? -4 : 4), top - 2);
        ctx.restore();
    },
};

// Total in the doughnut hole, as the old SVG donut had.
const centerTotalPlugin = {
    id: "reportCenterTotal",
    afterDraw(chart, args, opts) {
        const total = opts && opts.total;
        if (total === undefined || total === null) return;

        const meta = chart.getDatasetMeta(0);
        if (!meta || !meta.data.length) return;

        const { x, y } = meta.data[0];
        const ctx = chart.ctx;
        ctx.save();
        ctx.fillStyle = bodyColor();
        ctx.font = "600 16px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(total), x, y);
        ctx.restore();
    },
};

export default {
    name: "ReportChart",
    props: {
        type: {
            type: String,
            default: "bar",
            validator: (v) => ["bar", "stacked", "donut"].includes(v),
        },
        series: {
            type: Array,
            default: () => [],
        },
        target: {
            type: Number,
            default: 0,
        },
        targetLabel: {
            type: String,
            default: "min",
        },
        valueSuffix: {
            type: String,
            default: "",
        },
        emptyText: {
            type: String,
            default: "No data to chart yet.",
        },
    },
    data() {
        return {
            chart: null,
        };
    },
    computed: {
        canvasHeight() {
            if (this.type === "donut") return "240px";
            return Math.min(650, this.series.length * 30 + 48) + "px";
        },
        donutTotal() {
            return this.series.reduce((sum, item) => sum + this.num(item.value), 0);
        },
        summary() {
            if (!this.series.length) return this.emptyText;
            return this.series.map((item) => `${item.label} ${this.itemValue(item)}${this.valueSuffix}`).join(", ");
        },
    },
    watch: {
        series: {
            deep: true,
            handler: "rebuild",
        },
        target: "rebuild",
        type: "rebuild",
    },
    mounted() {
        this.build();
    },
    beforeDestroy() {
        this.destroy();
    },
    methods: {
        num(v) {
            const n = Number(v);
            return Number.isFinite(n) && n > 0 ? n : 0;
        },
        itemValue(item) {
            if (this.type === "stacked" && Array.isArray(item.segments)) {
                return item.segments.reduce((sum, seg) => sum + this.num(seg.value), 0);
            }
            return this.num(item.value);
        },
        destroy() {
            if (this.chart) {
                this.chart.destroy();
                this.chart = null;
            }
        },
        // Datasets change shape between renders (stacked segment counts, row counts), so a full
        // re-create is both simpler and cheap at these sizes.
        rebuild() {
            this.destroy();
            this.$nextTick(this.build);
        },
        build() {
            if (!this.series.length || !this.$refs.canvas) return;
            this.chart = new Chart(this.$refs.canvas.getContext("2d"), this.chartConfig());
        },
        chartConfig() {
            return this.type === "donut" ? this.donutConfig() : this.barConfig();
        },
        barConfig() {
            const suffix = this.valueSuffix;
            const hints = this.series.map((item) => item.hint || "");
            const stacked = this.type === "stacked";

            return {
                type: "bar",
                data: {
                    labels: this.series.map((item) => item.label),
                    datasets: stacked ? this.stackedDatasets() : this.simpleDatasets(),
                },
                options: {
                    indexAxis: "y",
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: { top: 14 } },
                    scales: {
                        x: {
                            beginAtZero: true,
                            stacked,
                            ticks: { color: bodyColor(), precision: 0 },
                            grid: { color: gridColor() },
                        },
                        y: {
                            stacked,
                            ticks: { color: bodyColor(), autoSkip: false },
                            grid: { display: false },
                        },
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => `${ctx.parsed.x}${suffix}`,
                                afterBody: (items) => {
                                    const hint = items.length ? hints[items[0].dataIndex] : "";
                                    return hint ? [hint] : [];
                                },
                            },
                        },
                        reportTarget: {
                            value: Number(this.target) || 0,
                            label: this.targetLabel,
                        },
                    },
                },
                plugins: [targetLinePlugin],
            };
        },
        simpleDatasets() {
            return [
                {
                    data: this.series.map((item) => this.itemValue(item)),
                    backgroundColor: this.series.map((item) => resolveColor(item.color)),
                    borderWidth: 0,
                    borderRadius: 2,
                    barThickness: 14,
                },
            ];
        },
        stackedDatasets() {
            const depth = this.series.reduce(
                (max, item) => Math.max(max, Array.isArray(item.segments) ? item.segments.length : 0),
                0
            );

            return Array.from({ length: depth }, (unused, i) => ({
                data: this.series.map((item) => {
                    const segments = Array.isArray(item.segments) ? item.segments : [];
                    return segments[i] ? this.num(segments[i].value) : 0;
                }),
                backgroundColor: this.series.map((item) => {
                    const segments = Array.isArray(item.segments) ? item.segments : [];
                    return resolveColor(segments[i] ? segments[i].class : item.color);
                }),
                borderWidth: 0,
                barThickness: 14,
            }));
        },
        donutConfig() {
            const suffix = this.valueSuffix;

            return {
                type: "doughnut",
                data: {
                    labels: this.series.map((item) => item.label),
                    datasets: [
                        {
                            data: this.series.map((item) => this.num(item.value)),
                            backgroundColor: this.series.map((item) => resolveColor(item.color)),
                            borderWidth: 0,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: "65%",
                    plugins: {
                        legend: {
                            position: "right",
                            labels: { color: bodyColor(), boxWidth: 10, boxHeight: 10, usePointStyle: true },
                        },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => `${ctx.label}: ${ctx.parsed}${suffix}`,
                            },
                        },
                        reportCenterTotal: { total: this.donutTotal },
                    },
                },
                plugins: [centerTotalPlugin],
            };
        },
    },
};
</script>

<style scoped>
.report-chart-canvas {
    position: relative;
    width: 100%;
}
</style>
